import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ForgotPasswordOtp.css';
import { apiUrl } from '../api';

function ForgotPasswordOtp() {
  const navigate = useNavigate();

  // Step state: 1 = Email, 2 = OTP, 3 = New Password
  const [step, setStep] = useState(1);

  // Form fields
  const [email, setEmail] = useState('');
  const [otpBoxes, setOtpBoxes] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Status state
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Refs for the 6 input boxes
  const inputRefs = useRef([]);

  // Handle single digit typing & auto-focus
  const handleOtpChange = (index, value) => {
    // Only allow single numeric digit
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otpBoxes];
    newOtp[index] = value.slice(-1); // Take the latest typed digit
    setOtpBoxes(newOtp);

    // Auto-advance to next input if digit is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle Backspace navigation
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpBoxes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle full 6-digit paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtpBoxes(digits);
      inputRefs.current[5]?.focus();
    }
  };

  // Step 1: Send OTP to Email
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(apiUrl('/api/auth/forgot-password-otp'), { email });
      setMessage(response.data?.message || 'OTP sent successfully to your email.');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Email not found or failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const completeOtp = otpBoxes.join('');

    if (completeOtp.length !== 6) {
      setError('Please enter all 6 digits of the OTP.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      await axios.post(apiUrl('/api/auth/verify-otp'), { email, otp: completeOtp });
      setMessage('OTP verified. Set your new password.');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Update Password in DB
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    try {
      const completeOtp = otpBoxes.join('');
      const response = await axios.post(apiUrl('/api/auth/reset-password-otp'), {
        email,
        otp: completeOtp,
        password
      });

      setMessage(response.data?.message || 'Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // Optional: Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(apiUrl('/api/auth/forgot-password-otp'), { email });
      setMessage(response.data?.message || 'A new OTP has been sent.');
      setOtpBoxes(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        {/* Stepper Dots */}
        <div className="step-indicator">
          <span className={`step-dot ${step >= 1 ? 'active' : ''}`}>1</span>
          <span className="step-line"></span>
          <span className={`step-dot ${step >= 2 ? 'active' : ''}`}>2</span>
          <span className="step-line"></span>
          <span className={`step-dot ${step >= 3 ? 'active' : ''}`}>3</span>
        </div>

        {step === 1 && (
          <>
            <h1>Forgot Password</h1>
            <p className="auth-subtitle">Enter your email to receive an OTP.</p>
          </>
        )}

        {step === 2 && (
          <>
            <h1>Verify OTP</h1>
            <p className="auth-subtitle">Enter the 6-digit code sent to <strong>{email}</strong></p>
          </>
        )}

        {step === 3 && (
          <>
            <h1>Create New Password</h1>
            <p className="auth-subtitle">Choose a secure password for your account.</p>
          </>
        )}

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        {/* STEP 1: Enter Email */}
        {step === 1 && (
          <form onSubmit={handleSendOtp} className="auth-form">
            <div className="form-group">
              <label>Registered Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        )}

        {/* STEP 2: Enter OTP (6-box layout) */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="auth-form">
            <div className="form-group">
              <label>Enter 6-Digit Code</label>
              <div className="otp-inputs-container" onPaste={handleOtpPaste}>
                {otpBoxes.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    ref={(el) => (inputRefs.current[index] = el)}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="otp-box-input"
                    disabled={loading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <div className="resend-row">
              <button
                type="button"
                className="text-btn"
                onClick={handleResendOtp}
                disabled={loading}
              >
                Resend OTP
              </button>
              <button
                type="button"
                className="text-btn"
                onClick={() => {
                  setStep(1);
                  setOtpBoxes(['', '', '', '', '', '']);
                }}
                disabled={loading}
              >
                Change Email
              </button>
            </div>
          </form>
        )}

        {/* STEP 3: Enter New Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="auth-form">
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="auth-btn" disabled={loading}>
              {loading ? 'Updating Password...' : 'Reset Password'}
            </button>
          </form>
        )}

        <p className="auth-link">
          Remember password? <Link to="/login">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}

export default ForgotPasswordOtp;